<template>
  <div
    v-if="symbol"
    class="flex justify-start items-center gap-12 bg-neutral-primary p-2 rounded-md"
  >
    <div
      v-gid="{
        module: 'option',
        screen: 'home',
        section: 'optionSymbol',
        component: 'optionSymbolInfo',
      }"
      class="flex justify-start items-center gap-2 cursor-pointer"
      @click="updateActiveIsin(symbol.isin)"
    >
      <SymbolTradeStateIcon
        size="medium"
        :trading-state-code="symbol.instrumentTradingState"
      />

      <NText variant="caption2" weight="medium">
        {{ symbol.instrumentName }}
      </NText>
    </div>

    <div class="flex justify-start items-center gap-2">
      <NText variant="caption2" weight="medium" class="text-neutral-secondary">
        {{ t('symbol.last_trade_price') }}
      </NText>

      <div class="flex items-center">
        <NumberFormatter #="{ number }" :value="symbol.lastTradePrice">
          <ChangePriceEffect :price="symbol.lastTradePrice ?? 0">
            <NText
              :key="symbol.lastTradePrice"
              variant="caption2"
              weight="bold"
              class="ms-1"
              dir="ltr"
            >
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>

        <NumberFormatter
          #="{ number }"
          :value="(symbol.referencePriceVariationPercentage ?? 0) / 100"
          number-style="percent"
          maximum-fraction-digits="2"
        >
          <ChangePriceEffect
            :price="symbol.referencePriceVariationPercentage ?? 0"
          >
            <NText
              dir="ltr"
              variant="caption2"
              weight="bold"
              :class="{
                'text-brand-primary':
                  (symbol.referencePriceVariationPercentage ?? 0) > 0,
                'text-critical-primary':
                  (symbol.referencePriceVariationPercentage ?? 0) < 0,
              }"
              class="ms-1"
            >
              ({{ number }})
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>
      </div>
    </div>

    <div class="flex justify-start items-center gap-2">
      <NText variant="caption2" weight="medium" class="text-neutral-secondary">
        {{ t('modules.order.closingPrice') }}
      </NText>

      <div class="flex items-center">
        <NumberFormatter #="{ number }" :value="symbol.closingPrice ?? 0">
          <ChangePriceEffect :price="symbol.closingPrice ?? 0">
            <NText
              :key="symbol.closingPrice"
              variant="caption2"
              weight="bold"
              class="ms-1"
              dir="ltr"
            >
              {{ number }}
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>

        <NumberFormatter
          #="{ number }"
          :value="(symbol.closingPriceVariationPercentage ?? 0) / 100"
          number-style="percent"
          maximum-fraction-digits="2"
        >
          <ChangePriceEffect
            :price="symbol.closingPriceVariationPercentage ?? 0"
          >
            <NText
              dir="ltr"
              variant="caption2"
              weight="medium"
              :class="{
                'text-brand-primary':
                  (symbol.closingPriceVariationPercentage ?? 0) > 0,
                'text-critical-primary':
                  (symbol.closingPriceVariationPercentage ?? 0) < 0,
              }"
              class="ms-1"
            >
              ({{ number }})
            </NText>
          </ChangePriceEffect>
        </NumberFormatter>
      </div>
    </div>

    <div class="flex justify-start items-center gap-2">
      <NText variant="caption2" weight="medium" class="text-neutral-secondary">
        {{ t('symbol.trade_volume') }}
      </NText>

      <NumberFormatter
        #="{ number }"
        :value="symbol.totalNumberOfSharesTraded"
        maximum-fraction-digits="2"
      >
        <NText variant="caption2" weight="bold">
          {{ number }}
        </NText>
      </NumberFormatter>
    </div>

    <div class="flex justify-start items-center gap-2">
      <NText variant="caption2" weight="medium" class="text-neutral-secondary">
        {{ t('symbol.trade_value') }}
      </NText>

      <NumberFormatter
        #="{ number }"
        :value="symbol.totalTradeValue"
        maximum-fraction-digits="2"
      >
        <NText variant="caption2" weight="bold">
          {{ number }}
        </NText>
      </NumberFormatter>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { UnderlyingAssetIsin } from '@libs/api-reference';
import { SymbolTradeStateIcon, useActiveSymbolIsin } from '@/modules/symbol';

import NumberFormatter from '@/components/Number.vue';
import ChangePriceEffect from '@/components/ChangePriceEffect.vue';

const { t } = useI18n();

const { updateActiveIsin } = useActiveSymbolIsin();

defineProps<{
  isin: string;
  symbol: UnderlyingAssetIsin;
}>();
</script>
